import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { getFullName } from '@klareospace/pharos-lib-utils';
import type { UserWithResolvedScopes } from '@klareospace/pharos-lib-types';
import { ConfirmDialog } from '@klareospace/pharos-lib-web-utils';

import type { DriverAction } from '../types/driver.types';
import { DRIVER_ACTIONS } from '../types/driver.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DriverConfirmAction {
  driver: UserWithResolvedScopes;
  type: DriverAction;
}

export interface DriverConfirmDialogProps {
  confirmAction: DriverConfirmAction | null;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildDialogKey(type: DriverAction, field: string): string {
  return `drivers.actions.${type}.dialog.${field}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DriverConfirmDialog({
  confirmAction,
  onConfirm,
  onOpenChange,
}: DriverConfirmDialogProps): React.JSX.Element {
  const { t } = useTranslation('settings');
  const type = confirmAction?.type ?? DRIVER_ACTIONS.DEACTIVATE;
  const driverName = confirmAction ? getFullName(confirmAction.driver.firstName, confirmAction.driver.lastName) : '';

  return (
    <ConfirmDialog
      cancelLabel={t(buildDialogKey(type, 'cancel'))}
      confirmLabel={t(buildDialogKey(type, 'confirm'))}
      description={t(buildDialogKey(type, 'description'), { name: driverName })}
      destructive={type === DRIVER_ACTIONS.ARCHIVE}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
      open={!!confirmAction}
      title={t(buildDialogKey(type, 'title'))}
    />
  );
}
